import { getEndpoint } from '@/ui/core/config'
import { GuestbookEvent } from '@/shared/types'
import { GuestbookClient } from '../GuestbookClient'
import { ensureDefined } from '@/utils'
import { uploadImage } from './uploadImage'
import { deleteImage } from './deleteImage'

/**
 * The available properties to update on an event
 */
export interface UpdateEventInput extends Partial<
  Omit<GuestbookEvent, 'owner' | 'createdAt' | 'updatedAt'>
> {
  updatedWelcomeImage?: File
  imagesToDelete?: string[]
}

/**
 *
 * @param this The instance of Guestbook
 * @param input The parameters to update an event.
 * @returns
 */
export async function updateEvent(
  this: GuestbookClient,
  input: UpdateEventInput,
): Promise<string> {
  const jwtToken = await this.auth.getJWT()
  if (!jwtToken) {
    throw new Error('Missing token')
  }
  const owner = await this.auth.getUser()

  const { updatedWelcomeImage: file, imagesToDelete, ...props } = input
  const updatedInput = props

  // If the user is just wanting to delete an image do that.
  if (imagesToDelete && imagesToDelete.length > 0) {
    await deleteImage({
      url: imagesToDelete[0],
      location: 'events',
      jwtToken,
    })
  }

  // https://docs.aws.amazon.com/AWSJavaScriptSDK/v3/latest/Package/-aws-sdk-lib-storage/
  if (file) {
    // Check if it is an image, redundant with the accept property on input
    // but is meant to consider what this app will support in what contexts
    if (/^image\/?!(svg|gif)/.test(file.type)) {
      throw new Error('Not supported image types')
    }

    const imageUrl = await uploadImage({
      file,
      location: 'events',
      keyPrefix: owner,
      jwtToken,
    })

    // For now this is only supporting one image
    if (updatedInput.images && updatedInput.images.length > 0) {
      try {
        await deleteImage({
          url: updatedInput.images[0],
          location: 'events',
          jwtToken,
        })
      } catch (error) {
        // ignoring issue for now.
        console.warn('Issue deleting previous image', error)
      }
    }
    updatedInput.images = [imageUrl]
  }

  await fetch(getEndpoint('events'), {
    method: 'PUT',
    body: JSON.stringify(updatedInput),
    headers: {
      Authorization: jwtToken,
    },
  })

  return ensureDefined(updatedInput.id)
}
